'use client';

import { useEffect, useRef, useState } from 'react';

import type { SearchSuggestionItem } from '../types/search-bar';

export const DEFAULT_SUGGEST_URL = '/api/mock/search/suggestions?s=';
export const DEFAULT_DEBOUNCE_MS = 300;
export const DEFAULT_MIN_LENGTH = 2;
export const DEFAULT_MAX_RESULTS = 10;

type LegacySuggestionRaw = {
  label?: unknown;
  guid?: unknown;
  category_spanish?: unknown;
};

/**
 * Mapper por defecto: convierte la respuesta del endpoint legacy
 * (`{ label, guid, category_spanish, ... }`) al contrato del widget
 * (`SearchSuggestionItem`).
 */
export function defaultMapResponse(raw: unknown): SearchSuggestionItem[] {
  if (!raw) return [];
  const list = Array.isArray(raw) ? raw : Object.values(raw as Record<string, unknown>);

  const items: SearchSuggestionItem[] = [];
  list.forEach((entry, index) => {
    if (!entry || typeof entry !== 'object') return;
    const candidate = entry as LegacySuggestionRaw;
    if (typeof candidate.label !== 'string' || typeof candidate.guid !== 'string') return;

    const subtitle =
      typeof candidate.category_spanish === 'string' && candidate.category_spanish.length > 0
        ? candidate.category_spanish
        : undefined;

    items.push({
      id: candidate.guid || `suggestion-${index}`,
      title: candidate.label,
      href: candidate.guid,
      subtitle,
    });
  });
  return items;
}

export type UseSearchSuggestionsOptions = {
  /** URL base (terminada en `=`); la query se concatena url-encoded. */
  suggestUrl?: string;
  /** Mínimo de caracteres antes de buscar. */
  minLength?: number;
  /** Debounce entre keystroke y request, en ms. */
  debounceMs?: number;
  /** Tope de resultados a entregar al consumidor. */
  maxResults?: number;
  /** Fetch inyectable (útil para tests). */
  fetcher?: typeof fetch;
  /** Mapper de respuesta cruda → items. */
  mapResponse?: (raw: unknown) => SearchSuggestionItem[];
};

export type UseSearchSuggestionsResult = {
  items: readonly SearchSuggestionItem[];
  isLoading: boolean;
  error: Error | null;
};

const EMPTY_ITEMS: readonly SearchSuggestionItem[] = [];

/**
 * Sugerencias debounced con cancelación de la request previa.
 * No filtra en cliente: confía en el endpoint.
 */
export function useSearchSuggestions(
  query: string,
  options: UseSearchSuggestionsOptions = {},
): UseSearchSuggestionsResult {
  const {
    suggestUrl = DEFAULT_SUGGEST_URL,
    minLength = DEFAULT_MIN_LENGTH,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    maxResults = DEFAULT_MAX_RESULTS,
    fetcher,
    mapResponse = defaultMapResponse,
  } = options;

  const [items, setItems] = useState<readonly SearchSuggestionItem[]>(EMPTY_ITEMS);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetcherRef = useRef(fetcher);
  const mapResponseRef = useRef(mapResponse);
  fetcherRef.current = fetcher;
  mapResponseRef.current = mapResponse;

  useEffect(() => {
    const normalized = query.trim();
    if (normalized.length < minLength) {
      setItems(EMPTY_ITEMS);
      setIsLoading(false);
      setError(null);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    const timer = window.setTimeout(() => {
      const doFetch = fetcherRef.current ?? fetch;
      const url = `${suggestUrl}${encodeURIComponent(normalized)}`;

      doFetch(url, { signal: controller.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Suggestions request failed: ${response.status}`);
          }
          return response.json();
        })
        .then((raw) => {
          if (cancelled) return;
          const mapped = mapResponseRef.current(raw).slice(0, maxResults);
          setItems(mapped);
          setIsLoading(false);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          if (err instanceof DOMException && err.name === 'AbortError') return;
          setError(err instanceof Error ? err : new Error(String(err)));
          setItems(EMPTY_ITEMS);
          setIsLoading(false);
        });
    }, debounceMs);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, suggestUrl, minLength, debounceMs, maxResults]);

  return { items, isLoading, error };
}
